import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, CardBody, Card } from 'reactstrap';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { LinearProgress, SnackbarContent, Tooltip, Box, IconButton } from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import CancelIcon from '@mui/icons-material/Cancel';
import Badge from "react-bootstrap/Badge";
import { formatDate } from 'src/utils/DateUtils';
import OrderDetailsShippingViewDetailsModal from 'src/containers/PnD/Order/OrderCreation/View/components/OrderDetailsShippingViewDetailsModal';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import PageHeader from 'src/shared/components/layout/PageHeader';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';

const CancelledOrderViewIndex = () => {
  useEffect(() => {
    getAllCancelledOrdersDetails();
  }, [])

  const [reactTableData, setReactTableData] = useState({ tableRowsData: [] });
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);
  const [temp, setTempData] = useState("");
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [isTitle, setTitle] = useState(null);

  const getAllCancelledOrdersDetails = async () => {
    try {
      setLoading(true);
      let Obj = {};
      const response = await PndRestApi.GetAllCancelledOrders(Obj);
      if (response.status === 200 && response.data.status === 200) {
        const ordersList = response.data?.data || [];
        setReactTableData({ tableRowsData: ordersList });

        if (ordersList.length > 0) {
          setSelectedOrder(ordersList[0]);
        } else {
          setSelectedOrder(null);
        }
      } else if (response?.data?.status && response?.data?.status === 204) {
        setNoRecordFound(true);
        setReactTableData({ tableRowsData: [] });
        setSelectedOrder(null);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message:${response.data?.message}`,
        );
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (event) => {
    if (event?.orderId) {
      setTempData(event);
      setViewDetailPopup(true);
      setTitle("View-Order Details");
    }
  };

  const ORDER_COLUMNS = useMemo(() => [
    {
      key: 'orderNumber',
      header: 'Order No',
      sortable: true,
    },
    {
      key: 'erpOrderNo',
      header: 'ERP Order No',
      sortable: true,
    },
    // {
    //   key: 'createdDateTime',
    //   header: 'Order Date',
    //   sortable: true,
    //   render: (value) => value ? formatDate(value) : '—'
    // },
    {
      key: 'orderCancellationRequestedFrom',
      header: 'Cancelled From',
      sortable: true,
      render: (value) => value === 2 ? 'Merchant' : 'Own Hub'
    }
  ], []);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>
        <PageHeader
          icon={<CancelIcon fontSize="large" />}
          title="Cancelled Order Details"
          subtitle="View and audit cancelled customer orders."
          gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
        />

        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

        <Row style={{ marginTop: '20px' }}>
          {/* Left Panel: Master List */}
          <Col md={7} lg={7}>
            {!isNoRecordFound ? (
              <RadioSelectTable
                rows={reactTableData.tableRowsData || []}
                columns={ORDER_COLUMNS}
                rowKey="orderId"
                selectedKey={selectedOrder?.orderId}
                onSelect={setSelectedOrder}
                searchKeys={['orderNumber', 'erpOrderNo', 'erpNo', 'fromName', 'toName']}
                searchPlaceholder="Search orders..."
                pageSizes={[5, 10, 20, 50]}
              />
            ) : (
              <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center' }}>
                <p className="text-muted m-0">No cancelled orders found.</p>
              </Card>
            )}
          </Col>

          {/* Right Panel: Sleek Detail Card */}
          <Col md={5} lg={5}>
            <Card style={{
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
              minHeight: '520px',
              background: '#ffffff',
              overflow: 'hidden',
            }}>
              {selectedOrder ? (
                <>
                  {/* Header Banner */}
                  <div style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    padding: '24px 20px',
                    color: '#ffffff'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          {selectedOrder.orderNumber}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          Date: {selectedOrder.createdDateTime ? formatDate(selectedOrder.createdDateTime) : '—'}
                        </span>
                      </div>
                      <div>
                        <span
                          className="badge bg-white text-danger"
                          style={{ fontSize: '0.75rem', fontWeight: 700, padding: '5px 10px', borderRadius: '12px' }}
                        >
                          {selectedOrder.orderStatus || 'CANCELLED'}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                      <Tooltip title="View Order Details">
                        <IconButton
                          size="small"
                          onClick={() => viewDetails(selectedOrder)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Details Body */}
                  <CardBody style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                      {/* General Info */}
                      <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          General Info
                        </h6>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Cancelled From:</span>
                            <span className="fw-bold small text-dark">
                              {selectedOrder.orderCancellationRequestedFrom === 2 ? 'Merchant' : 'Own Hub'}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Order Type:</span>
                            <span className="fw-bold small">
                              {selectedOrder.orderType === 1 ? (
                                <Badge bg="success">DO</Badge>
                              ) : selectedOrder.orderType === 2 ? (
                                <Badge bg="primary">PU</Badge>
                              ) : (
                                <Badge bg="warning">PnD</Badge>
                              )}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Order Date:</span>
                            <span className="fw-bold small text-dark">
                              {selectedOrder.createdDateTime ? formatDate(selectedOrder.createdDateTime) : '—'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Route Details */}
                      <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          Route & Logistics
                        </h6>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="text-muted small fw-bold">From:</span>
                              <span>
                                {selectedOrder.fromTypeId === 1 ? (
                                  <Badge bg="secondary">OH</Badge>
                                ) : selectedOrder.fromTypeId === 2 ? (
                                  <Badge bg="secondary">HC</Badge>
                                ) : selectedOrder.fromTypeId === 3 ? (
                                  <Badge bg="secondary">M</Badge>
                                ) : selectedOrder.fromTypeId === 4 ? (
                                  <Badge bg="secondary">AH</Badge>
                                ) : <Badge bg="secondary">MC</Badge>}
                              </span>
                            </div>
                            <span className="small text-dark d-block fw-semibold">{selectedOrder.fromName || '—'}</span>
                          </div>

                          <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: '8px' }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="text-muted small fw-bold">To:</span>
                              <span>
                                {selectedOrder.toTypeId === 1 ? (
                                  <Badge bg="secondary">OH</Badge>
                                ) : selectedOrder.toTypeId === 2 ? (
                                  <Badge bg="secondary">HC</Badge>
                                ) : selectedOrder.toTypeId === 3 ? (
                                  <Badge bg="secondary">M</Badge>
                                ) : selectedOrder.toTypeId === 4 ? (
                                  <Badge bg="secondary">AH</Badge>
                                ) : <Badge bg="secondary">MC</Badge>}
                              </span>
                            </div>
                            <span className="small text-dark d-block fw-semibold">{selectedOrder.toName || '—'}</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </CardBody>
                </>
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
                }}>
                  <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>📦</div>
                  <h5 className="fw-bold text-muted">No Order Selected</h5>
                  <p className="text-muted small">Select an order from the list to view detailed information.</p>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {/* {isNoRecordFound && <SnackbarContent message={'No Record Found'} sx={{ mt: 2 }} />} */}

      {viewDetailPopup &&
        <OrderDetailsShippingViewDetailsModal modalShow={viewDetailPopup}
          setModalShow={setViewDetailPopup}
          dataInfo={temp}
          isTitle={isTitle} />
      }
    </Box>
  );
};

export default CancelledOrderViewIndex;